import { useMemo } from 'react';

import { useElementWidth } from '../../../../../common/ui/width-observer';
import { type AppOption } from '../../../../../common/utils/app-filter-options';
import { type AppPillLayout } from '../../lite-app-filter-bar/app-pill-layout';
import { useLiteAppOptions } from '../../lite-app-filter-bar/use-lite-app-options';
import {
	areAllAppsLabelsShownForWidth,
	maxVisibleAppsForWidth,
	REFRESH_FILTER_ROW_WIDTH_ID,
} from '../row-width';

export const DEFAULT_MAX_VISIBLE_APPS = 5;

// Below this many overflow apps, the More control costs more (a click, a popup, ~125px of
// its own) than the apps it would hide, so they render inline instead. Design hasn't
// confirmed whether the exact boundary is 3 or looser; QS-10754.
export const OVERFLOW_THRESHOLD_FOR_MORE_CONTROL = 3;

export const buildRefreshAppTileLayout = (maxVisibleApps: number): AppPillLayout => ({
	firstConnectedBatchSize: maxVisibleApps,
	unauthenticated3pSlots: 0,
	secondConnectedBatchSize: 0,
	maxVisibleApps,
});

// Trims the full app list to a width-derived visible count, then applies the same absorption
// rule as below (few enough leftover apps get folded back inline rather than left to a control
// that shows almost nothing). Shared by the tentative and final passes below so both agree on
// exactly the same rule for what ends up in overflow.
const deriveAppLists = (
	maxVisibleApps: number,
	labelledWidthBudget: number | undefined,
	fullVisibleOptions: AppOption[],
	fullOverflowOptions: AppOption[],
): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } => {
	const { visibleOptions: baseVisibleOptions, overflowOptions: baseOverflowOptions } =
		maxVisibleApps >= fullVisibleOptions.length
			? { visibleOptions: fullVisibleOptions, overflowOptions: fullOverflowOptions }
			: {
					visibleOptions: fullVisibleOptions.slice(0, maxVisibleApps),
					overflowOptions: [...fullVisibleOptions.slice(maxVisibleApps), ...fullOverflowOptions],
				};

	const oauthOverflowOptions = baseOverflowOptions.filter((option) => option.isUnauthenticated);
	const absorbableOverflowOptions = baseOverflowOptions.filter(
		(option) => !option.isUnauthenticated,
	);

	// Labelled pills are wide enough that folding them back can overflow the row.
	const fitsAbsorbed =
		labelledWidthBudget === undefined ||
		baseVisibleOptions.length + absorbableOverflowOptions.length <= labelledWidthBudget;

	if (
		absorbableOverflowOptions.length > 0 &&
		absorbableOverflowOptions.length < OVERFLOW_THRESHOLD_FOR_MORE_CONTROL &&
		fitsAbsorbed
	) {
		return {
			visibleOptions: [...baseVisibleOptions, ...absorbableOverflowOptions],
			overflowOptions: oauthOverflowOptions,
		};
	}

	return { visibleOptions: baseVisibleOptions, overflowOptions: baseOverflowOptions };
};

export const useRefreshAppOptions = ({
	maxVisibleApps: maxVisibleAppsOverride = DEFAULT_MAX_VISIBLE_APPS,
}: { maxVisibleApps?: number } = {}): {
	visibleOptions: AppOption[];
	overflowOptions: AppOption[];
	areAllAppsLabelsShown: boolean;
	/** The selected app swapped in from overflow, if any. It always sits in the last tile. */
	promotedValue?: AppOption['value'];
} => {
	// Fetched with the override alone — never the width-derived count — so selection status
	// and the overflow count below are stable inputs to the width budget, not its output.
	// `useLiteAppOptions` already reads the current selection itself; deriving `isAppSelected`
	// from its result avoids a second, search-page-relay-local RSS dependency.
	const overrideLayout = useMemo(
		() => buildRefreshAppTileLayout(maxVisibleAppsOverride),
		[maxVisibleAppsOverride],
	);
	const { visibleOptions: fullVisibleOptions, overflowOptions: fullOverflowOptions } =
		useLiteAppOptions(overrideLayout, { isRefreshedFilterRow: true });

	const isAppSelected = useMemo(
		() => [...fullVisibleOptions, ...fullOverflowOptions].some((option) => option.isSelected),
		[fullVisibleOptions, fullOverflowOptions],
	);

	const rowWidth = useElementWidth(REFRESH_FILTER_ROW_WIDTH_ID);
	const areAllAppsLabelsShown = areAllAppsLabelsShownForWidth(rowWidth);
	const areAppsLabelled = areAllAppsLabelsShown && !isAppSelected;

	// Reserving width for More apps shrinks the tile budget, which can only push more tiles
	// into overflow, never fewer — so first check whether the control would still end up
	// rendering without reserving anything. If it wouldn't, that unreserved budget is already
	// the right answer. If it would, reserving can only add to that overflow (never remove from
	// it), so it's still going to render once reserved — one extra pass settles it exactly,
	// without an open-ended loop.
	const unreservedBudget = maxVisibleAppsForWidth({
		rowWidth,
		isAppSelected,
		areAppsLabelled,
		willRenderMoreApps: false,
	});
	const maxVisibleAppsUnreserved =
		unreservedBudget === undefined
			? maxVisibleAppsOverride
			: Math.min(maxVisibleAppsOverride, unreservedBudget);
	const wouldRenderMoreAppsUnreserved =
		deriveAppLists(
			maxVisibleAppsUnreserved,
			areAppsLabelled ? unreservedBudget : undefined,
			fullVisibleOptions,
			fullOverflowOptions,
		).overflowOptions.length > 0;

	const widthBudget = wouldRenderMoreAppsUnreserved
		? maxVisibleAppsForWidth({ rowWidth, isAppSelected, areAppsLabelled, willRenderMoreApps: true })
		: unreservedBudget;
	const maxVisibleApps =
		widthBudget === undefined
			? maxVisibleAppsOverride
			: Math.min(maxVisibleAppsOverride, widthBudget);

	// Trim the already-ranked visible list down to the width-derived count, pushing the
	// displaced tail back to the front of overflow, then fold a small leftover back inline
	// instead of leaving the More control to hold almost nothing. Apps that still need to
	// connect are exempt from folding back: the dropdown is the only surface with a connect
	// affordance, so they stay there no matter how few of them there are — which also never
	// fights the selected-app rule below, since an app that needs to connect can't be selected.
	const { visibleOptions, overflowOptions } = useMemo(
		() =>
			deriveAppLists(
				maxVisibleApps,
				areAppsLabelled ? widthBudget : undefined,
				fullVisibleOptions,
				fullOverflowOptions,
			),
		[maxVisibleApps, areAppsLabelled, widthBudget, fullVisibleOptions, fullOverflowOptions],
	);

	// The selected app must always be visible. If it's the one sitting in the overflow list
	// (selected from the More apps dropdown), swap it into the row's last tile in place of
	// whatever was there — purely derived from the current selection, so it disappears again
	// the moment something else is selected instead.
	return useMemo(() => {
		const selectedIndex = overflowOptions.findIndex((option) => option.isSelected);

		if (selectedIndex === -1 || visibleOptions.length === 0) {
			return { visibleOptions, overflowOptions, areAllAppsLabelsShown };
		}

		const selectedOption = overflowOptions[selectedIndex];
		const displacedOption = visibleOptions[visibleOptions.length - 1];

		return {
			visibleOptions: [...visibleOptions.slice(0, -1), selectedOption],
			promotedValue: selectedOption.value,
			overflowOptions: [
				displacedOption,
				...overflowOptions.slice(0, selectedIndex),
				...overflowOptions.slice(selectedIndex + 1),
			],
			areAllAppsLabelsShown,
		};
	}, [visibleOptions, overflowOptions, areAllAppsLabelsShown]);
};
